export const webPaths = {
  getNewsletterSuccessPath: (): string => "/newsletter/bestaetigung/",
  getBlogPath: (): string => "/blog/",
  getBlogArticlePath: (slug: string): string => `/blog/${slug}/`,
  getPodcastPath: (): string => "/podcast/",
  getSitemapXmlPath: (): string => "/sitemap.xml",
  getPodcastEpisodesPath: (): string => "/podcast/folgen/",
  getPodcastEpisodeNumberPath: (episodeNumber: string): string => `/podcast/folgen/${episodeNumber}/`,
  getPodcastEpisodeNumberSlugPath: (episodeNumber: string, slug: string): string =>
    `/podcast/folgen/${episodeNumber}/${slug}/`,
  getDataProtectionPath: (): string => "/datenschutz/",
  getUserDeleteRequestAppPath: (): string => "/app/rechtliches/user-delete/",
  getImprintPath: (): string => "/impressum/",
  getTermsPath: (): string => "/agb/",
  getDisclaimerPath: (): string => "/haftungsausschluss/",
  getPartnerProgramTermsPath: (): string => "/partnerprogramm-bedingungen/",
  getContactPath: (): string => "/kontakt/",
  getB2BAppEnquiryPath: (licensesAmount?: number): string =>
    `/fuer-unternehmen/anfragen-app${licensesAmount ? `?licensesAmount=${String(licensesAmount)}` : ""}`,
  getResellerEnquiryPath: (): string => `/fuer-vertriebspartner/bewerben`,
  getLoginPath: (): string => "/login/",
  getSigninPath: (): string => "/registrieren/",
  /** Firebase Auth email links (verify email, reset password, change email) — set this URL in Firebase Console templates */
  getAuthActionPath: (): string => "/auth/action/",
  getForgotPasswordPath: (): string => "/login/passwort-vergessen/",
  getDashboardPath: (): string => "/dashboard/",
  getProfilePath: (): string => "/dashboard/profil/",
  /** § 312k BGB public contract cancellation (no login required) */
  getContractCancellationPath: (): string => "/vertraege-kuendigen/",
  /** E-mail confirmation link target for contract cancellation */
  getContractCancellationConfirmPath: (): string => "/vertraege-kuendigen/bestaetigen/",
  /** § 356a BGB withdrawal hub */
  getWithdrawalPath: (): string => "/widerruf/",
  /** Logged-in shortcut to Stripe self-service contract cancellation */
  getMembershipCancellationPath: (): string => "/dashboard/vertrag-kuendigen/",
  /** Legacy v1 legal texts for users who declined the v2 agreement migration */
  getLegacyLegalDashboardPath: (): string => "/dashboard/vertragsbedingungen/",
  getLegacyLegalDashboardPagePath: (slug: string): string => `/dashboard/vertragsbedingungen/${slug}/`,
  getLegacyLegalAppDashboardPagePath: (slug: string): string => `/dashboard/vertragsbedingungen/app/${slug}/`,
  getPromoCodeRedeemPath: (): string => "/dashboard/promo-code/",
  getPromoCodeRedeemPublicPath: (code?: string): string => `/app/promo-code/${code ? `?code=${code}` : ""}`,
  /** Public signed-URL download route for files under `web/downloads/` in Storage. */
  getPublicWebDownloadPath: (relativePath: string): string =>
    `/downloads/${relativePath
      .split("/")
      .filter((segment) => segment.length > 0)
      .map((segment) => encodeURIComponent(segment.normalize("NFC")))
      .join("/")}`,
  getAppPath: (): string => "/app/",
  getAppBuyPath: (code?: string): string => `/app/kaufen/${code ? `?code=${code}` : ""}`,
  getAppBuySuccessPath: (): string => "/app/kaufen/erfolg/",
  getAppAuthorsPath: (): string => "/app/autoren/",
  getAppAuthorPath: (slug: string): string => `/app/autoren/${slug}/`,
  getAppAudioCoursesPath: (): string => "/app/audiokurse/",
  getAppAudioCourseCategoriesPath: (): string => "/app/audiokurse/kategorien/",
  getAppAudioCoursePath: (slug: string, openApp?: boolean): string =>
    `/app/audiokurse/${slug}/${openApp ? "?openApp=1" : ""}`,
  getAppAudioCourseAuthorPath: (authorSlug: string): string => `/app/audiokurse/autoren/${authorSlug}/`,
  getAppAudioCourseCategoryPath: (categorySlug: string): string => `/app/audiokurse/kategorien/${categorySlug}/`,
  getAppMeditationsPath: (): string => "/app/meditationen/",
  getAppMeditationPath: (slug: string, openApp?: boolean): string =>
    `/app/meditationen/${slug}/${openApp ? "?openApp=1" : ""}`,
  getAppMeditationAuthorPath: (authorSlug: string): string => `/app/meditationen/autoren/${authorSlug}/`,
  getAppPomodoroTimerPath: (): string => "/app/pomodoro-timer/",
  /** Campaign paths */
  getHausforderungenSchlafproblemePath: (): string => `/herausforderungen/schlafprobleme/`,
};

export const nativePaths = {
  // Root path
  getRedirectPath: (): string => "/",

  // Auth paths
  getOnboardingDataPath: (): string => "/onboarding/data",
  getOnboardingLegalPath: (): string => "/onboarding/legal",
  getOnboardingNewsletterPath: (): string => "/onboarding/newsletter",
  getOnboardingPackagePath: (): string => "/onboarding/package",
  getForgotPasswordPath: (): string => "/forgot-password",
  getLoginPath: (): string => "/login",
  getSignInPath: (): string => "/signin",
  getVerifyPath: (): string => "/verify",
  getWelcomePath: (): string => "/welcome",

  // Public paths
  getChangelogPath: (): string => "/changelog",
  getDataProtectionPath: (): string => "/data-protection",
  getDisclaimerPath: (): string => "/disclaimer",
  getImprintPath: (): string => "/imprint",
  getLicensesPath: (): string => "/licenses",
  getTermsConditionsPath: (): string => "/terms-conditions",

  // Offline
  getOfflinePath: (): string => "/offline",

  getTodayPath: (): string => "/today",

  // Audio Courses
  getAudioCoursesPath: (): string => "/audioCourses",
  getSharedAudioCoursesPath: (): string => "/shared/audioCourses",
  getAudioCoursePath: (courseId: string): string => `/courses/audioCourses/${courseId}`,
  getAudioCourseFilePath: (courseId: string, fileAsKebabCase: string): string =>
    `/courses/audioCourses/${courseId}/${fileAsKebabCase}`,
  getAudioCourseFinishedPath: (courseId: string, fileAsKebabCase: string): string =>
    `/courses/audioCourses/${courseId}/${fileAsKebabCase}/finished`,
  getSharedAudioCoursePath: (slug: string): string => `/shared/audioCourses/${slug}`,

  // Meditations
  getMeditationsPath: (): string => "/meditations",
  getSharedMeditationsPath: (): string => "/shared/meditations",
  getMeditationPath: (meditationId: string): string => `/meditations/${meditationId}`,
  getMeditationPlayerPath: (meditationId: string): string => `/meditations/${meditationId}/player`,
  getMeditationPlayerFinishedPath: (meditationId: string): string => `/meditations/${meditationId}/player/finished`,
  getSharedMeditationPath: (slug: string): string => `/shared/meditations/${slug}`,

  // Authors and Contributors
  getAuthorPath: (id: string): string => `/authors/${id}`,
  getContributorsPath: (): string => "/contributors",

  // Intro Courses
  getIntroCoursePath: (courseId: string): string => `/courses/introCourses/${courseId}`,
  getIntroCourseFilePath: (courseId: string, fileAsKebabCase: string): string =>
    `/courses/introCourses/${courseId}/${fileAsKebabCase}`,
  getIntroCourseFinishedPath: (courseId: string, fileAsKebabCase: string): string =>
    `/courses/introCourses/${courseId}/${fileAsKebabCase}/finished`,

  // Profile and Settings
  getProfilePath: (): string => "/profile",
  getMyDownloadsPath: (): string => "/profile/downloads",
  getJourneyPath: (): string => "/profile/journey",
  /** @deprecated Prefer `getJourneyPath`. */
  getReisePath: (): string => "/profile/journey",
  /** @deprecated Prefer `getJourneyPath` — kept for older deep links. */
  getEtappeBadgesPath: (): string => "/profile/journey",
  getActivityCalendarPath: (): string => "/profile/activity-calendar",
  getAppSettingsPath: (): string => "/profile/app-settings",
  getProfileSettingsPath: (): string => "/profile/profile-settings",
  // Subscription
  getSubscriptionPath: (): string => "/subscription",
  getWithdrawalWebPath: (): string => webPaths.getWithdrawalPath(),
  getContractCancellationWebPath: (): string => webPaths.getContractCancellationPath(),
  getSubscriptionPayedPath: (): string => "/subscription/payed",
  getSubscriptionTransferredPath: (): string => "/subscription/transferred",

  // Timeline and Tools
  getTimelinePath: (): string => "/timeline",
  getPomodoroTimerPath: (): string => "/tools/pomodoro-timer",
  getSilentMeditationOnboardingPath: (): string => "/tools/silent-meditation/",
  getSilentMeditationPlayerPath: (duration: number): string =>
    `/tools/silent-meditation/player?duration=${String(duration)}`,
  getSilentMeditationFinalisationPath: (durationMeditatedInSeconds: number): string =>
    `/tools/silent-meditation/finalisation?durationMeditatedInSeconds=${String(durationMeditatedInSeconds)}`,

  // Misc
  getFeedbackPath: (): string => "/feedback",
  getNewsletterPath: (): string => "/newsletter",
  getRecommendationsPath: (): string => "/recommendations",
};
